/**
 * Sistema (1.0 AdminSystem sin secretos): salud de Functions, tipos de cambio del BCE (con
 * refresco), `settings/environment` (modo mantenimiento con mensaje por idioma y versión mínima de
 * las apps) e interruptores. Los secretos viven en Secret Manager, no aquí.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import type { SystemInfoOutput } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, str, Table, td } from '../lib/ui.js';
import { flagInfo, WIRED_FLAGS } from '../lib/flags.js';

type Doc = Record<string, unknown>;
const SEMVER = /^\d+(\.\d+){0,2}$/;

interface EnvForm {
  env: 'sandbox' | 'prod';
  maintenance: boolean;
  message_es: string;
  message_en: string;
  android: string;
  ios: string;
}

export function SystemPage(): ReactElement {
  const [info, setInfo] = useState<SystemInfoOutput | null>(null);
  const [form, setForm] = useState<EnvForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    call(api.admin.systemInfo, {})
      .then(setInfo)
      .catch((e: unknown) => {
        setError(errorText(e));
      });
    getDoc(doc(db, 'settings/environment'))
      .then((s) => {
        const d: Doc = s.data() ?? {};
        const msg = (d.maintenance_message as Doc | null | undefined) ?? {};
        const mav = (d.min_app_version as Doc | undefined) ?? {};
        setForm({
          env: d.env === 'prod' ? 'prod' : 'sandbox',
          maintenance: d.maintenance === true,
          message_es: str(msg.es),
          message_en: str(msg.en),
          android: str(mav.android),
          ios: str(mav.ios),
        });
      })
      .catch(() => {
        setForm({
          env: 'sandbox',
          maintenance: false,
          message_es: '',
          message_en: '',
          android: '',
          ios: '',
        });
      });
  };
  useEffect(load, []);
  const refreshFx = (): void => {
    setBusy(true);
    call(api.admin.refreshFxRates, {})
      .then(() => {
        setNotice('Tipos de cambio actualizados desde el BCE.');
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const toggleFlag = (key: string, on: boolean): void => {
    if (!info) return;
    setBusy(true);
    setError(null);
    call(api.admin.updateSettings, {
      doc: 'featureFlags',
      data: { flags: { ...info.feature_flags, [key]: on } },
      reason: `${flagInfo(key).name}: ${on ? 'encendido' : 'apagado'}`,
    })
      .then(() => {
        setNotice(`«${flagInfo(key).name}» ${on ? 'encendido' : 'apagado'}.`);
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const testAlert = (): void => {
    setBusy(true);
    call(api.admin.testAlert, {})
      .then((r) => {
        setNotice(
          `Aviso de prueba enviado: ${r.pushed} móviles con «nuvvu Admin», ${r.emailed} correos (Ajustes › Empresa › correos de avisos).`,
        );
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const saveEnv = (): void => {
    if (!form) return;
    for (const v of [form.android, form.ios])
      if (v && !SEMVER.test(v.trim())) {
        setError(`Versión no válida «${v}»: usa el formato 1.0.15.`);
        return;
      }
    if (form.maintenance && !form.message_es.trim()) {
      setError('Con el modo mantenimiento activo hace falta el mensaje en español.');
      return;
    }
    const message: Record<string, string> = {};
    if (form.message_es.trim()) message.es = form.message_es.trim();
    if (form.message_en.trim()) message.en = form.message_en.trim();
    setBusy(true);
    setError(null);
    call(api.admin.updateSettings, {
      doc: 'environment',
      data: {
        env: form.env,
        maintenance: form.maintenance,
        maintenance_message: Object.keys(message).length ? message : null,
        min_app_version: { android: form.android.trim(), ios: form.ios.trim() },
      },
      reason: form.maintenance ? 'modo mantenimiento' : 'entorno',
    })
      .then(() => {
        setNotice('settings/environment guardado.');
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const set = (k: keyof EnvForm, v: string | boolean): void => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Sistema</h1>
      <Notice text={notice} />
      <Notice text={error} error />
      {!info && !error && <p style={muted}>Cargando…</p>}
      {info && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 12,
          }}
        >
          <section style={card}>
            <strong>Salud de Functions</strong>
            <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
              <span>
                Entorno: <code>{info.health.env}</code> · Versión:{' '}
                <code>{info.health.version}</code> · Node: <code>{info.health.node}</code>
              </span>
              <span>
                Usuarios: {info.counts.users.toLocaleString('es')} · Anuncios activos:{' '}
                {info.counts.products_active.toLocaleString('es')} · Pedidos abiertos:{' '}
                {info.counts.orders_open.toLocaleString('es')}
              </span>
            </div>
          </section>
          <section style={card}>
            <strong>Avisos al admin</strong>
            <span style={{ fontSize: 13 }}>
              Ventas, quejas, tickets, chat de soporte, denuncias, bloqueos, avisos DSA,
              contracargos y el resumen de las 8:00 llegan por correo a la lista de{' '}
              <Link to="/ajustes/company">Ajustes › Empresa</Link> y por notificación a los móviles
              con la app «nuvvu Admin».
            </span>
            <button type="button" style={btn(false)} disabled={busy} onClick={testAlert}>
              Enviar un aviso de prueba
            </button>
          </section>
          <section style={{ ...card, gridColumn: '1 / -1' }}>
            <strong>Interruptores</strong>
            <span style={muted}>
              Se aplican al momento, sin desplegar (el servidor tarda hasta 1 minuto en notarlo).
            </span>
            {(
              [
                'Inteligencia artificial',
                'Seguridad y moderación',
                'Funciones de la plataforma',
              ] as const
            ).map((group) => (
              <div key={group} style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 13, marginTop: 6 }}>{group}</span>
                {Object.entries(info.feature_flags)
                  .filter(([k]) => flagInfo(k).group === group)
                  .sort(([a], [b]) => Number(WIRED_FLAGS.has(b)) - Number(WIRED_FLAGS.has(a)))
                  .map(([k, v]) => {
                    const f = flagInfo(k);
                    const wired = WIRED_FLAGS.has(k);
                    return (
                      <label
                        key={k}
                        title={k}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 12,
                          border: '1px solid #e5e7eb',
                          opacity: wired ? 1 : 0.55,
                          cursor: wired && !busy ? 'pointer' : 'default',
                        }}
                      >
                        <span style={{ flex: 1, display: 'grid', gap: 2 }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>{f.name}</span>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>
                            {wired ? f.hint : 'Aún no conectado: cambiarlo no hace nada todavía.'}
                          </span>
                        </span>
                        <input
                          type="checkbox"
                          role="switch"
                          checked={v}
                          disabled={!wired || busy}
                          onChange={(e) => {
                            toggleFlag(k, e.target.checked);
                          }}
                          style={{ width: 22, height: 22, accentColor: '#F56A47' }}
                        />
                      </label>
                    );
                  })}
              </div>
            ))}
            <Link to="/ajustes/featureFlags" style={{ fontSize: 12 }}>
              Edición avanzada
            </Link>
          </section>
          <section style={card}>
            <strong>Tipos de cambio (BCE, base EUR)</strong>
            <span style={muted}>
              {info.fx.updated_at_ms
                ? `Actualizados ${new Date(info.fx.updated_at_ms).toLocaleString('es')} (caché 12 h)`
                : 'Sin caché todavía.'}
            </span>
            <Table head={['Moneda', '1 EUR =']}>
              {Object.entries(info.fx.rates)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([c, r]) => (
                  <tr key={c}>
                    <td style={td}>{c}</td>
                    <td style={td}>{r}</td>
                  </tr>
                ))}
            </Table>
            <button
              type="button"
              style={{ ...btn(false), justifySelf: 'start' }}
              disabled={busy}
              onClick={refreshFx}
            >
              Actualizar ahora
            </button>
          </section>
        </div>
      )}
      {form && (
        <form
          style={{ ...card, display: 'grid', gap: 10, maxWidth: 640 }}
          onSubmit={(e) => {
            e.preventDefault();
            saveEnv();
          }}
        >
          <strong>Entorno (settings/environment)</strong>
          <p style={muted}>
            El modo mantenimiento muestra un aviso en toda la web (sección 17: solo durante la
            migración de datos). La versión mínima la leen las apps (fase 4): por debajo piden
            actualizar. Vacío = sin aviso.
          </p>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.maintenance}
              onChange={(e) => {
                set('maintenance', e.target.checked);
              }}
            />
            Modo mantenimiento
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Mensaje (español)
            <input
              style={field}
              value={form.message_es}
              onChange={(e) => {
                set('message_es', e.target.value);
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Mensaje (inglés, opcional)
            <input
              style={field}
              value={form.message_en}
              onChange={(e) => {
                set('message_en', e.target.value);
              }}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Versión mínima Android
              <input
                style={field}
                value={form.android}
                placeholder="1.0.0"
                onChange={(e) => {
                  set('android', e.target.value);
                }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Versión mínima iOS
              <input
                style={field}
                value={form.ios}
                placeholder="1.0.0"
                onChange={(e) => {
                  set('ios', e.target.value);
                }}
              />
            </label>
          </div>
          <span style={muted}>Entorno: {form.env} (no se cambia desde aquí).</span>
          <button type="submit" style={{ ...btn(true), justifySelf: 'start' }} disabled={busy}>
            Guardar
          </button>
        </form>
      )}
    </>
  );
}
